/**
 * Dual Eye Selector for Contact Lenses
 * Allows customers to select different variants for left and right eye
 * and adds both to cart simultaneously
 */

class DualEyeSelector extends HTMLElement {
  constructor() {
    super();
    
    this.leftEyeSelect = this.querySelector('[data-left-eye-select]');
    this.rightEyeSelect = this.querySelector('[data-right-eye-select]');
    this.addToCartButton = this.querySelector('[data-dual-eye-cart-button]');
    this.errorMessage = this.querySelector('[data-dual-eye-error]');
    this.successMessage = this.querySelector('[data-dual-eye-success]');
    
    this.productId = this.dataset.productId;
    
    this.init();
  }

  init() {
    if (!this.leftEyeSelect || !this.rightEyeSelect || !this.addToCartButton) {
      console.error('Dual Eye Selector: Required elements not found');
      return;
    }

    // Add event listeners
    this.addToCartButton.addEventListener('click', this.handleAddToCart.bind(this));
    
    // Update button state when selections change
    this.leftEyeSelect.addEventListener('change', this.updateButtonState.bind(this));
    this.rightEyeSelect.addEventListener('change', this.updateButtonState.bind(this));
    
    // Initial button state
    this.updateButtonState();
  }

  updateButtonState() {
    const leftEyeSelected = this.leftEyeSelect.value && this.leftEyeSelect.value !== '';
    const rightEyeSelected = this.rightEyeSelect.value && this.rightEyeSelect.value !== '';
    
    this.addToCartButton.disabled = !(leftEyeSelected && rightEyeSelected);
  }

  async handleAddToCart(event) {
    event.preventDefault();
    
    const leftEyeVariantId = this.leftEyeSelect.value;
    const rightEyeVariantId = this.rightEyeSelect.value;
    
    // Validate selections
    if (!leftEyeVariantId || !rightEyeVariantId) {
      this.showError('Please select variants for both eyes');
      return;
    }

    // Get the quantity from the quantity selector
    const quantityInput = document.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

    // Disable button and show loading state
    this.addToCartButton.disabled = true;
    this.addToCartButton.classList.add('loading');
    this.hideMessages();

    try {
      // Add both items to cart with the selected quantity
      const items = [
        {
          id: leftEyeVariantId,
          quantity: quantity,
          properties: {
            'Eye': 'Left'
          }
        },
        {
          id: rightEyeVariantId,
          quantity: quantity,
          properties: {
            'Eye': 'Right'
          }
        }
      ];

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || 'Failed to add items to cart');
      }

      // Success! Show success message
      this.showSuccess('Beide artikelen zijn succesvol aan je winkelwagen toegevoegd!');
      
      // Open cart drawer and show the items
      this.openCartDrawer(data);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(error.message || 'Failed to add items to cart');
    } finally {
      // Re-enable button and remove loading state
      this.addToCartButton.disabled = false;
      this.addToCartButton.classList.remove('loading');
      this.updateButtonState();
    }
  }

  dispatchCartUpdateEvent() {
    // Fetch updated cart data
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update cart icon bubble count
        this.updateCartCount(cart.item_count);
        
        // Dispatch custom event that other components can listen to
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cart }
        }));
        
        // Try to update cart drawer if it exists
        this.updateCartDrawer();
        
        // Try to update cart notification if it exists
        this.updateCartNotification();
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
      });
  }

  updateCartCount(itemCount) {
    // Update all cart count elements
    const cartCountElements = document.querySelectorAll('.cart-count-bubble span, [data-cart-count]');
    cartCountElements.forEach(element => {
      element.textContent = itemCount;
      
      // Show/hide bubble based on count
      const bubble = element.closest('.cart-count-bubble');
      if (bubble) {
        if (itemCount > 0) {
          bubble.classList.remove('hidden');
        } else {
          bubble.classList.add('hidden');
        }
      }
    });
  }

  updateCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Try different methods to update the drawer
      if (typeof cartDrawer.getSectionsToRender === 'function') {
        cartDrawer.getSectionsToRender().forEach((section) => {
          fetch(`${window.Shopify.routes.root}?sections=${section.id}`)
            .then(response => response.json())
            .then(data => {
              const html = data[section.id];
              if (html && section.selector) {
                const element = document.querySelector(section.selector);
                if (element) {
                  element.innerHTML = html;
                }
              }
            });
        });
      } else if (typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents();
      }
    }
  }

  updateCartNotification() {
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && typeof cartNotification.renderContents === 'function') {
      cartNotification.renderContents();
    }
  }

  async openCartDrawer(cartData) {
    const cartDrawer = document.querySelector('cart-drawer');
    
    if (!cartDrawer) {
      console.error('Cart drawer not found');
      return;
    }

    try {
      // Fetch the updated cart sections
      const response = await fetch(`${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await response.json();
      
      // Create a parsed state object similar to what the theme expects
      const parsedState = {
        sections: sections
      };
      
      // Remove is-empty class from cart-drawer before opening
      cartDrawer.classList.remove('is-empty');
      
      // Call renderContents if it exists (this is what product-form.js does)
      if (typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(parsedState);
        
        // Make sure is-empty is removed after render as well
        setTimeout(() => {
          cartDrawer.classList.remove('is-empty');
          const drawerInner = cartDrawer.querySelector('.drawer__inner');
          if (drawerInner) {
            drawerInner.classList.remove('is-empty');
          }
          const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
          if (cartDrawerItems) {
            cartDrawerItems.classList.remove('is-empty');
          }
        }, 50);
      } else {
        // Fallback: manually update and open
        await this.updateCartSections(sections);
        
        // Remove is-empty class from all elements
        cartDrawer.classList.remove('is-empty');
        const drawerInner = cartDrawer.querySelector('.drawer__inner');
        if (drawerInner) {
          drawerInner.classList.remove('is-empty');
        }
        const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          cartDrawerItems.classList.remove('is-empty');
        }
        
        // Open the drawer
        if (typeof cartDrawer.open === 'function') {
          setTimeout(() => {
            cartDrawer.open(this.addToCartButton);
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error opening cart drawer:', error);
      // Fallback: just click the cart icon
      const cartIcon = document.querySelector('#cart-icon-bubble');
      if (cartIcon) {
        cartIcon.click();
      }
    }
  }

  async updateCartSections(sections) {
    // Update cart icon bubble
    if (sections['cart-icon-bubble']) {
      const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newIconBubble = html.querySelector('.shopify-section');
      const currentIconBubble = document.querySelector('#shopify-section-cart-icon-bubble');
      if (newIconBubble && currentIconBubble) {
        currentIconBubble.innerHTML = newIconBubble.innerHTML;
      }
    }

    // Update cart drawer
    if (sections['cart-drawer']) {
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const newDrawerInner = html.querySelector('.drawer__inner');
      const currentDrawerInner = document.querySelector('cart-drawer .drawer__inner');
      if (newDrawerInner && currentDrawerInner) {
        currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      }
    }
  }

  showError(message) {
    this.hideMessages();
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.classList.add('active');
    }
  }

  showSuccess(message) {
    this.hideMessages();
    if (this.successMessage) {
      this.successMessage.textContent = message;
      this.successMessage.classList.add('active');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        this.successMessage.classList.remove('active');
      }, 3000);
    }
  }

  hideMessages() {
    if (this.errorMessage) {
      this.errorMessage.classList.remove('active');
    }
    if (this.successMessage) {
      this.successMessage.classList.remove('active');
    }
  }
}

// Register the custom element
if (typeof customElements !== 'undefined') {
  customElements.define('dual-eye-selector', DualEyeSelector);
}

